import { FETCH_FINISHED_ORDERS, FETCH_ORDER, FETCH_PENDING_ORDERS, RESET_ORDERS } from 'reducers/actions-types';
import axios from 'axios';
import { dispatchError } from 'reducers/helpers';
import { finishedOrders, order, orderFeedback, orderPickerRating, pendingOrders } from 'constants/api-urls';
import logError from 'util/misc/log-error';

export default Object.freeze({
    /**
     * Get order by id
     * @param {string} id
     * @return {function(*=): Promise<AxiosResponse<IOrder>>}
     */
    getOrder(id) {
        return function(dispatch) {
            return axios
                .get(order(id))
                .then(
                    /**
                     * @param {IOrder} data
                     */
                    ({ data }) => {
                        dispatch({ type: FETCH_ORDER, order: data });
                        return data;
                    })
                .catch(e => {
                    // log the error on sentry only if it's not a 404 response
                    const shouldLog = e && e.status !== 404;

                    if (shouldLog) {
                        dispatchError(dispatch, e, { url: pendingOrders(), description: e.message });
                    }

                    return Promise.reject(e);
                });
        };
    },

    /**
     * Get pending orders
     * @param {number} limit
     * @param {number} offset
     * @return {function(*=): Promise<AxiosResponse<IOrderList> | never>}
     */
    getPendingOrders({ limit, offset }) {
        return function(dispatch) {
            return axios
                .get(pendingOrders(), {
                    params: {
                        limit: limit,
                        offset: offset,
                    },
                })
                .then(
                    /**
                     * @param {IOrderList} data
                     */
                    ({ data }) => {
                        dispatch({ type: FETCH_PENDING_ORDERS, pendingOrders: data });
                        return data;
                    })
                .catch(e => {
                    dispatchError(dispatch, e, {
                        url: pendingOrders(),
                        description: e.message,
                    });
                });
        };
    },

    /**
     * @param {number} limit
     * @param {number} offset
     * @return {function(*=): Promise<AxiosResponse<IOrderList> | never>}
     */
    getFinishedOrders({ limit, offset }) {
        return function(dispatch) {
            return axios
                .get(finishedOrders(), {
                    params: {
                        limit: limit,
                        offset: offset,
                    },
                })
                .then(
                    /**
                     * @param {IOrderList} data
                     */
                    ({ data }) => {
                        dispatch({ type: FETCH_FINISHED_ORDERS, finishedOrders: data });
                        return data;
                    })
                .catch(e => {
                    dispatchError(dispatch, e, {
                        url: pendingOrders(),
                        description: e.message,
                    });
                });
        };
    },

    /**
     * Set order picker rating
     * @param {number} id - order id
     * @param {IPickerRatingPayload} payload
     * @return {function(*=): Promise<AxiosResponse<any> | never>}
     */
    setPickerRating({ id, payload }) {
        return function(dispatch) {
            return axios
                .post(orderPickerRating(id), payload)
                .then(({ data: result, status }) => {
                    if (status > 399) {
                        logError(new Error(`Unable to set picker rating "status=${ status }"`));
                    }
                    return result;
                })
                .catch(e => {
                    dispatchError(dispatch, e, {
                        url: orderPickerRating(),
                        description: e.message,
                    });
                });
        };
    },

    /**
     * Set order feedback
     * @param {string} id - order id
     * @param {IOrderFeedbackPayload} payload
     * @return {function(*=): Promise<AxiosResponse<IOrderFeedbackPayload> | never>}
     */
    setFeedback({ id, payload }) {
        return function(dispatch) {
            return axios
                .post(orderFeedback(id), payload)
                .then(
                    /**
                     * @param {IOrderFeedbackPayload} result
                     * @param {number} status
                     */
                    ({ data: result, status }) => {
                        if (status > 399) {
                            logError(new Error(`Unable to set picker rating "status=${ status }"`));
                        }
                        return result;
                    })
                .catch(e => {
                    dispatchError(dispatch, e, {
                        url: orderFeedback(),
                        description: e.message,
                    });
                });
        };
    },

    reset() {
        return { type: RESET_ORDERS };
    },
});



// WEBPACK FOOTER //
// ./mcart/assets/src/js/app/reducers/modules/orders/actions.js